/**
 * PATIENT-CRM-TIMELINE-01 — the read-only patient 360 journey
 * (`/dashboard/patients/:patientId/timeline`).
 *
 * Open a patient and see their full journey assembled from data that
 * ALREADY EXISTS: the linked lead's lifecycle events (rendered via the new
 * vocabulary labels), the appointment behavioral history (each status as it
 * occurred, with the canonical current-state projection), the patient-master
 * link anchor, and the advisory crossmatch verdict.
 *
 * DISPLAY-ONLY: this surface renders a chronological story. It exposes NO
 * action that links, merges, or creates — those are the patient-master merge
 * state machine, a separate gated surface. PHI-ZERO payload (opaque ids +
 * UMR + controlled labels + status tokens + timestamps + counts).
 *
 * Gated by `RequirePatientMaster` (the server-computed
 * `me.patientMasterEnabled` signal). With the flag off the guard redirects,
 * so this page never mounts and `fetchPatientTimeline` never fires.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { fetchPatientTimeline } from '@/dashboard/api';
import { PageHeader, SectionShell, type BreadcrumbItem } from '@/dashboard/ui';
import {
  appointmentChipClass,
  appointmentLinkageLabel,
  crossmatchCaption,
  laneLabel,
  stageLabel,
  timelineWhen,
  walkinEntryCount,
  walkinLinkCaption,
  type PatientTimelineResponse,
} from '@/dashboard/patients/patient-timeline-view';
import { deskRecordHref } from '@/dashboard/patients/patient-master-view';

export interface PatientTimelineContentProps {
  readonly loading: boolean;
  readonly error: string | null;
  readonly timeline: PatientTimelineResponse | null;
}

/**
 * Pure presentational body (no fetch) — rendered directly in tests via
 * react-dom/server (wrapped in a Router for the back link).
 */
export function PatientTimelineContent({
  loading,
  error,
  timeline,
}: PatientTimelineContentProps): JSX.Element {
  if (loading) {
    return (
      <p className="loading" role="status" aria-live="polite">
        Loading journey…
      </p>
    );
  }
  if (error) {
    return (
      <p className="text-sm text-destructive" role="alert">
        {error}
      </p>
    );
  }
  if (!timeline) {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        Patient journey not found.
      </p>
    );
  }

  const stage = stageLabel(timeline.currentStage);
  const crossmatch = crossmatchCaption(timeline.crossmatch);

  // F4 (record anatomy): the journey page adopts the SAME header + breadcrumb
  // grammar as the record surfaces -- a lawful back-path (Patients -> the
  // patient record -> this journey), one h1, and labeled context subs (never a
  // middot-spliced prose line).
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Patients', to: '/dashboard/patients' },
    {
      label: 'Patient record',
      // L-18: stamp the door travelled, so the record's Back returns HERE.
      to: deskRecordHref(timeline.patientId, 'timeline'),
    },
    { label: 'Journey' },
  ];

  return (
    <section aria-label="Patient journey">
      <PageHeader
        eyebrow="Patient 360"
        title="Patient journey"
        breadcrumbs={breadcrumbs}
        testId="patient-timeline-header"
        subs={[
          <span key="umr" data-testid="timeline-umr">
            Patient number:{' '}
            <span className="font-mono">{timeline.umr}</span>
          </span>,
          timeline.status !== 'active' ? (
            <span key="status">Status: {timeline.status}</span>
          ) : null,
          stage !== null ? <span key="stage">Stage: {stage}</span> : null,
          crossmatch !== null ? (
            <span key="crossmatch" data-testid="timeline-crossmatch">
              {crossmatch}
            </span>
          ) : null,
        ]}
      />

      <div className="mt-4 grid max-w-3xl gap-3">
        <SectionShell
          eyebrow="History"
          title="Appointment history"
          testId="timeline-appointment-history"
        >
          {timeline.appointmentHistory.length === 0 ? (
            <p className="text-muted-foreground">No appointment activity yet.</p>
          ) : (
            <>
              {timeline.appointmentCurrent !== null ? (
                <p className="mb-2 text-muted-foreground">
                  Current state:{' '}
                  <span
                    className={`rounded-full border px-2 py-0.5 text-xs ${appointmentChipClass(
                      timeline.appointmentCurrent,
                    )}`}
                  >
                    {timeline.appointmentCurrent}
                  </span>
                </p>
              ) : null}
              <ol className="space-y-1">
                {timeline.appointmentHistory.map((obs, i) => {
                  const linkage = appointmentLinkageLabel(obs.linkage);
                  return (
                    <li key={`appt:${i}`}>
                      <span className="font-medium">{obs.storedLabel}</span>
                      {obs.canonicalLabel !== null &&
                      obs.canonicalLabel !== obs.storedLabel ? (
                        <span className="text-muted-foreground">
                          {' '}
                          → {obs.canonicalLabel}
                        </span>
                      ) : null}{' '}
                      <span className="text-xs text-muted-foreground">
                        ({timelineWhen(obs.occurredAt)})
                      </span>
                      {linkage !== null ? (
                        <span
                          className="ml-2 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground"
                          data-testid="timeline-appt-linkage"
                        >
                          {linkage}
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ol>
            </>
          )}
        </SectionShell>

        <SectionShell eyebrow="Journey" title="Journey" testId="timeline-journey">
          {timeline.entries.length === 0 ? (
            <p className="text-muted-foreground">
              No journey activity recorded yet.
            </p>
          ) : (
            <ol className="space-y-2" data-testid="timeline-entries">
              {timeline.entries.map((entry, i) => (
                <li
                  key={`entry:${i}`}
                  className="border-l-2 border-border pl-3"
                >
                  <p>
                    <span className="mr-2 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground">
                      {laneLabel(entry.lane)}
                    </span>
                    <span className="font-medium">{entry.label}</span>
                    {entry.appointment !== null ? (
                      <span
                        className={`ml-2 rounded-full border px-2 py-0.5 text-xs ${appointmentChipClass(
                          entry.appointment.canonicalStatus,
                        )}`}
                      >
                        {entry.appointment.storedLabel}
                      </span>
                    ) : null}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {timelineWhen(entry.occurredAt)}
                    {entry.clinicLabel !== null
                      ? ` · ${entry.clinicLabel}`
                      : ''}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </SectionShell>

        <p className="text-xs text-muted-foreground">
          Read-only view.{' '}
          {walkinLinkCaption(
            timeline.walkinLinkStatus,
            walkinEntryCount(timeline),
          )}{' '}
          {timeline.provenance.caveat}
        </p>
      </div>
    </section>
  );
}

export default function PatientTimelinePage(): JSX.Element {
  const { patientId } = useParams<{ patientId: string }>();
  const [timeline, setTimeline] = useState<PatientTimelineResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!patientId) {
      setLoading(false);
      setError('Missing patient id');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPatientTimeline(patientId)
      .then((res) => {
        if (cancelled) return;
        setTimeline(res);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : 'Failed to load patient journey',
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  return (
    <PatientTimelineContent
      loading={loading}
      error={error}
      timeline={timeline}
    />
  );
}
